import { useState, useEffect, useCallback, useRef } from "react";
import Project from "./Project";
import { logger } from "../logger";
import type {
  Project as ProjectType,
  EditingProject,
  EditingActionItem,
  FocusState,
} from "./types";

interface SingleProjectViewProps {
  project: ProjectType;
  onUpdateProject: (project: ProjectType) => void;
  className?: string;
}

export default function SingleProjectView({
  project,
  onUpdateProject,
  className = "",
}: SingleProjectViewProps) {
  const [editingProject, setEditingProject] = useState<EditingProject | null>(
    null,
  );
  const [editingActionItem, setEditingActionItem] =
    useState<EditingActionItem | null>(null);
  const [focusState, setFocusState] = useState<FocusState>({
    mode: "actionItems",
    projectIndex: 0,
    actionItemIndex: -1,
    expandedProjectId: project.id,
  });

  const transitionInProgress = useRef(false);

  const handleEditProject = (_projectIndex: number, fieldIndex: number) => {
    setEditingProject({
      id: project.id,
      name: project.name,
      desiredOutcome: project.desiredOutcome,
      fieldIndex,
    });
  };

  const handleProjectFieldChange = (
    field: keyof EditingProject,
    value: string,
  ) => {
    setEditingProject((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleProjectSave = () => {
    if (editingProject) {
      const updatedProject: ProjectType = {
        ...project,
        name: editingProject.name,
        desiredOutcome: editingProject.desiredOutcome,
      };
      onUpdateProject(updatedProject);
      setEditingProject(null);
    }
  };

  const handleProjectCancel = () => {
    setEditingProject(null);
  };

  const handleEditActionItem = (
    projectId: number,
    actionIndex: number,
    fieldIndex: number,
  ) => {
    const actionItem = project.actionItems[actionIndex];
    if (actionItem) {
      setEditingActionItem({
        id: actionItem.id,
        content: actionItem.content,
        date: actionItem.date || "",
        status: actionItem.status,
        delegatedTo: actionItem.delegatedTo || "",
        fieldIndex,
        projectId,
        actionIndex,
      });
    }
  };

  const handleAddNewActionItem = (projectId: number) => {
    setEditingActionItem({
      id: -1,
      content: "",
      date: "",
      status: "actionable",
      delegatedTo: "",
      fieldIndex: 0,
      projectId,
      actionIndex: -1,
    });
  };

  const handleActionItemFieldChange = (
    field: keyof EditingActionItem,
    value: string | number,
  ) => {
    setEditingActionItem((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleActionItemSave = () => {
    if (!editingActionItem) return;

    const updatedProject = { ...project };

    if (editingActionItem.actionIndex === -1) {
      // Adding new action item
      const newActionItem = {
        id: Date.now(),
        content: editingActionItem.content.trim(),
        date: editingActionItem.date || undefined,
        status: editingActionItem.status,
        delegatedTo: editingActionItem.delegatedTo || undefined,
      };
      updatedProject.actionItems = [
        ...updatedProject.actionItems,
        newActionItem,
      ];
    } else {
      // Updating existing action item
      updatedProject.actionItems = updatedProject.actionItems.map(
        (item, index) =>
          index === editingActionItem.actionIndex
            ? {
                ...item,
                content: editingActionItem.content,
                date: editingActionItem.date || undefined,
                status: editingActionItem.status,
                delegatedTo: editingActionItem.delegatedTo || undefined,
              }
            : item,
      );
    }

    onUpdateProject(updatedProject);
    setEditingActionItem(null);
  };

  const handleActionItemCancel = () => {
    setEditingActionItem(null);
  };

  /*
   * BROWSER FOCUS SYNCHRONIZATION FOR SINGLE PROJECT VIEW
   * Handles focus detection for action items within the single project
   */
  const handleFocusChange = useCallback(
    (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      logger.debug("SingleProjectView focus change triggered", {
        tagName: target.tagName,
        className: target.className,
      });

      // SKIP FOCUS HANDLING DURING ATOMIC TRANSITIONS
      if (transitionInProgress.current) {
        logger.debug("Skipping focus change - transition in progress");
        return;
      }

      // SKIP FOCUS HANDLING DURING EDIT MODES
      if (
        focusState.mode === "editProject" ||
        focusState.mode === "editActionItem"
      ) {
        logger.debug("Skipping focus change - in edit mode", {
          mode: focusState.mode,
        });
        return;
      }

      // SKIP FOCUS HANDLING ON INPUT FIELDS
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      ) {
        logger.debug("Skipping focus change - focus on form field", {
          tagName: target.tagName,
        });
        return;
      }

      /*
       * DETECT FOCUS ON ACTION ITEM CONTAINERS
       */
      const actionItemElement = target.closest("[data-action-item-index]");
      if (actionItemElement) {
        const actionIndex = parseInt(
          actionItemElement.getAttribute("data-action-item-index") || "0",
        );

        logger.debug("Focus detected on action item element", { actionIndex });
        logger.debug("Setting focus state: actionItems mode");

        setFocusState((prev) => ({
          ...prev,
          mode: "actionItems",
          actionItemIndex: actionIndex,
        }));
        return;
      }

      /*
       * DETECT FOCUS ON "ADD NEW ACTION ITEM" CARD
       */
      const newActionItemCard = target.closest("[data-new-action-item]");
      if (newActionItemCard) {
        logger.debug("Focus detected on Add New Action Item card");
        logger.debug("Setting focus state: actionItems mode, new item");
        setFocusState((prev) => ({
          ...prev,
          mode: "actionItems",
          actionItemIndex: -1,
        }));
        return;
      }
    },
    [focusState.mode],
  );

  // Set up focus event listeners
  useEffect(() => {
    window.addEventListener("focusin", handleFocusChange);
    return () => {
      window.removeEventListener("focusin", handleFocusChange);
    };
  }, [handleFocusChange]);

  // Auto-focus the "Add New Action Item" button when component mounts
  useEffect(() => {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      const addNewButton = document.querySelector(
        "[data-new-action-item]",
      ) as HTMLElement;
      if (addNewButton) {
        addNewButton.focus();
      }
    }, 100);
  }, []);

  return (
    <div className={`max-w-4xl mx-auto p-6 ${className}`}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Project View</h1>
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>Single Project:</strong> Focus on one project and its action
          items
        </div>
      </div>

      <Project
        project={project}
        projectIndex={0}
        isExpanded={true}
        isFocused={false}
        onToggleExpansion={() => {}} // No-op in single project view
        hideCollapseButton={true}
        onEditProject={handleEditProject}
        onEditActionItem={handleEditActionItem}
        onAddNewActionItem={handleAddNewActionItem}
        onActionItemFieldChange={handleActionItemFieldChange}
        onActionItemSave={handleActionItemSave}
        onActionItemCancel={handleActionItemCancel}
        editingProject={editingProject || undefined}
        editingActionItem={editingActionItem || undefined}
        onProjectFieldChange={handleProjectFieldChange}
        onProjectSave={handleProjectSave}
        onProjectCancel={handleProjectCancel}
        actionItemFocusState={{
          itemIndex: focusState.actionItemIndex,
          isAddingNew: focusState.actionItemIndex === -1,
        }}
      />
    </div>
  );
}
